import { useState, useEffect } from "react";
import { useAuth, API, useDateFilter } from "../App";
import axios from "axios";
import { toast } from "sonner";
import Layout from "../components/Layout";
import DateRangePicker from "../components/DateRangePicker";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Checkbox } from "../components/ui/checkbox";
import { Plus, CalendarIcon, Pencil, Trash2, Search, Filter } from "lucide-react";
import { format, parseISO } from "date-fns";
import { es } from "date-fns/locale";
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import ImportDialog from "../components/ImportDialog";
// Entrada Rápida - Para desactivar, comenta la siguiente línea y el componente <QuickEntryForm /> más abajo
import QuickEntryForm from "../components/QuickEntryForm";

const CHART_COLORS = ["#F43F5E", "#10B981", "#F59E0B", "#3B82F6", "#8B5CF6", "#EC4899", "#14B8A6", "#F97316"];

const TransactionsPage = () => {
  const { getAuthHeaders } = useAuth();
  const { startDate, endDate } = useDateFilter();
  const [transactions, setTransactions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [deletingBulk, setDeletingBulk] = useState(false);
  
  // Filters
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterPaymentMethod, setFilterPaymentMethod] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");

  // Form state
  const [formData, setFormData] = useState({
    date: new Date(),
    description: "",
    asset: "",
    amount: "",
    category_id: "",
    payment_method_id: "",
    branch_id: "",
    invoiced: false,
    notes: "",
    type: "expense"
  });
  const [branches, setBranches] = useState([]);
  const [filterBranch, setFilterBranch] = useState("all");

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    fetchTransactions();
  }, [startDate, endDate, filterCategory, filterPaymentMethod, filterBranch]);

  const fetchData = async () => {
    try {
      const [categoriesRes, methodsRes, branchesRes] = await Promise.all([
        axios.get(`${API}/categories`, getAuthHeaders()),
        axios.get(`${API}/payment-methods`, getAuthHeaders()),
        axios.get(`${API}/branches`, getAuthHeaders()),
      ]);
      setCategories(categoriesRes.data);
      setPaymentMethods(methodsRes.data);
      setBranches(branchesRes.data || []);
    } catch (error) {
      toast.error("Error al cargar los datos");
    }
  };

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      const startStr = format(startDate, 'yyyy-MM-dd');
      const endStr = format(endDate, 'yyyy-MM-dd');
      // Always filter by expense type since this page is for expenses only
      let url = `${API}/transactions?start_date=${startStr}&end_date=${endStr}&type=expense`;
      if (filterCategory !== "all") url += `&category_id=${filterCategory}`;
      if (filterPaymentMethod !== "all") url += `&payment_method_id=${filterPaymentMethod}`;
      if (filterBranch !== "all") url += `&branch_id=${filterBranch}`;
      
      const response = await axios.get(url, getAuthHeaders());
      setTransactions(response.data);
    } catch (error) {
      toast.error("Error al cargar los gastos");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.branch_id) {
      toast.error("Selecciona una sucursal");
      return;
    }
    try {
      const data = {
        ...formData,
        date: format(formData.date, "yyyy-MM-dd"),
        amount: parseFloat(formData.amount),
        type: "expense"  // Always expense
      };

      if (editingTransaction) {
        await axios.put(`${API}/transactions/${editingTransaction.id}`, data, getAuthHeaders());
        toast.success("Gasto actualizado");
      } else {
        await axios.post(`${API}/transactions`, data, getAuthHeaders());
        toast.success("Gasto registrado");
      }
      
      setDialogOpen(false);
      resetForm();
      fetchTransactions();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar el gasto");
    }
  };

  // Quick Entry save handler
  const handleQuickSave = async (data) => {
    try {
      await axios.post(`${API}/transactions`, data, getAuthHeaders());
      toast.success("✓ Guardado", { duration: 1500 });
      fetchTransactions();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar");
      throw error;
    }
  };

  const handleEdit = (transaction) => {
    setEditingTransaction(transaction);
    setFormData({
      date: parseISO(transaction.date),
      description: transaction.description,
      asset: transaction.asset || "",
      amount: transaction.amount.toString(),
      category_id: transaction.category_id,
      payment_method_id: transaction.payment_method_id,
      branch_id: transaction.branch_id || "",
      invoiced: transaction.invoiced,
      notes: transaction.notes || "",
      type: transaction.type
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!confirm("¿Estás seguro de eliminar esta transacción?")) return;
    
    try {
      await axios.delete(`${API}/transactions/${id}`, getAuthHeaders());
      toast.success("Transacción eliminada");
      fetchTransactions();
    } catch (error) {
      toast.error("Error al eliminar la transacción");
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedIds(filteredTransactions.map(t => t.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id, checked) => {
    if (checked) {
      setSelectedIds([...selectedIds, id]);
    } else {
      setSelectedIds(selectedIds.filter(i => i !== id));
    }
  };

  const [bulkUpdating, setBulkUpdating] = useState(false);
  const [bulkCategoryId, setBulkCategoryId] = useState("");

  const handleBulkUpdateCategory = async () => {
    if (selectedIds.length === 0) {
      toast.error("Selecciona al menos una transacción");
      return;
    }
    if (!bulkCategoryId) {
      toast.error("Selecciona la categoría destino");
      return;
    }
    const targetCat = categories.find(c => c.id === bulkCategoryId);
    if (!confirm(`¿Cambiar la categoría de ${selectedIds.length} transacciones a "${targetCat?.name || 'la elegida'}"?`)) return;

    setBulkUpdating(true);
    try {
      const response = await axios.post(
        `${API}/transactions/bulk-update-category`,
        { transaction_ids: selectedIds, category_id: bulkCategoryId },
        getAuthHeaders()
      );
      toast.success(response.data.message);
      setSelectedIds([]);
      setBulkCategoryId("");
      fetchTransactions();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al actualizar las categorías");
    } finally {
      setBulkUpdating(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    if (!confirm(`¿Estás seguro de eliminar ${selectedIds.length} transacciones?`)) return;
    
    setDeletingBulk(true);
    try {
      await axios.post(`${API}/transactions/bulk-delete`, selectedIds, getAuthHeaders());
      toast.success(`${selectedIds.length} transacciones eliminadas`);
      setSelectedIds([]);
      fetchTransactions();
    } catch (error) {
      toast.error("Error al eliminar las transacciones");
    } finally {
      setDeletingBulk(false);
    }
  };

  const resetForm = () => {
    setEditingTransaction(null);
    setFormData({
      date: new Date(),
      description: "",
      asset: "",
      amount: "",
      category_id: "",
      payment_method_id: "",
      branch_id: "",
      invoiced: false,
      notes: "",
      type: "expense"
    });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("es-MX", {
      style: "currency",
      currency: "MXN",
    }).format(value);
  };

  const filteredTransactions = transactions.filter(t => {
    const matchesSearch = t.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.notes?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesPayment = filterPaymentMethod === "all" || t.payment_method_id === filterPaymentMethod;
    return matchesSearch && matchesPayment;
  });

  const expenseCategories = categories.filter(c => c.type === "expense");
  const incomeCategories = categories.filter(c => c.type === "income");

  // Group transactions by category for pie chart (use filtered data)
  const expensesByCategory = Object.values(
    filteredTransactions.reduce((acc, t) => {
      const catName = t.category_name || "Sin categoría";
      const cat = categories.find(c => c.id === t.category_id);
      if (!acc[catName]) {
        acc[catName] = { name: catName, value: 0, color: cat?.color || "#6B7280" };
      }
      acc[catName].value += t.amount;
      return acc;
    }, {})
  );

  // Group transactions by payment method for bar chart (use filtered data)
  const expensesByPaymentMethod = Object.values(
    filteredTransactions.reduce((acc, t) => {
      const pmName = t.payment_method_name || "Sin método";
      if (!acc[pmName]) {
        acc[pmName] = { name: pmName, value: 0 };
      }
      acc[pmName].value += t.amount;
      return acc;
    }, {})
  );

  return (
    <Layout>
      <div className="space-y-6" data-testid="transactions-page">
        {/* Header */}
        <div className="flex flex-col gap-4">
          <div>
            <h1 className="text-2xl font-bold text-[#4A4A4A] font-['Manrope']">Gastos</h1>
            <p className="text-[#6B7280]">Registra y gestiona los gastos del negocio</p>
          </div>
          
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
            <ImportDialog onImportComplete={fetchTransactions} />
            
            <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
              <DialogTrigger asChild>
                <Button className="bg-[#F43F5E] hover:bg-[#E11D48] text-white btn-glow w-full sm:w-auto" data-testid="new-transaction-button">
                  <Plus className="w-4 h-4 mr-2" />
                  Nuevo Gasto
                </Button>
              </DialogTrigger>
            <DialogContent className="bg-white border-[#FECDD3] text-[#4A4A4A] max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-['Manrope']">
                  {editingTransaction ? "Editar Gasto" : "Nuevo Gasto"}
                </DialogTitle>
              </DialogHeader>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Date */}
                <div className="space-y-2">
                  <Label className="text-[#6B7280]">Fecha</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button 
                        variant="outline" 
                        className="w-full justify-start text-left bg-[#FEE2E2] border-[#FECDD3] text-[#4A4A4A] hover:bg-[#FEE2E2]"
                        data-testid="transaction-date-picker"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {format(formData.date, "PPP", { locale: es })}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-[#FEE2E2] border-[#FECDD3]" align="start">
                      <Calendar
                        mode="single"
                        selected={formData.date}
                        onSelect={(date) => date && setFormData({...formData, date})}
                        locale={es}
                        className="rounded-md"
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                {/* Description */}
                <div className="space-y-2">
                  <Label className="text-[#6B7280]">Descripción</Label>
                  <Input
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    placeholder="Ej: Renta del local"
                    required
                    data-testid="transaction-description-input"
                    className="bg-[#FEE2E2] border-[#FECDD3] text-[#4A4A4A] placeholder:text-[#6B7280]/50"
                  />
                </div>

                {/* Amount */}
                <div className="space-y-2">
                  <Label className="text-[#6B7280]">Monto (MXN)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.amount}
                    onChange={(e) => setFormData({...formData, amount: e.target.value})}
                    placeholder="0.00"
                    required
                    data-testid="transaction-amount-input"
                    className="bg-[#FEE2E2] border-[#FECDD3] text-[#4A4A4A] placeholder:text-[#6B7280]/50 font-mono"
                  />
                </div>

                {/* Category */}
                <div className="space-y-2">
                  <Label className="text-[#6B7280]">Categoría</Label>
                  <Select value={formData.category_id} onValueChange={(v) => setFormData({...formData, category_id: v})}>
                    <SelectTrigger className="bg-[#FEE2E2] border-[#FECDD3] text-[#4A4A4A]" data-testid="transaction-category-select">
                      <SelectValue placeholder="Seleccionar categoría" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#FEE2E2] border-[#FECDD3]">
                      {expenseCategories.map((cat) => (
                        <SelectItem key={cat.id} value={cat.id} className="text-[#4A4A4A] hover:bg-[#FEE2E2]">
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: cat.color }}></div>
                            {cat.name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Payment Method */}
                <div className="space-y-2">
                  <Label className="text-[#6B7280]">Método de Pago</Label>
                  <Select value={formData.payment_method_id} onValueChange={(v) => setFormData({...formData, payment_method_id: v})}>
                    <SelectTrigger className="bg-[#FEE2E2] border-[#FECDD3] text-[#4A4A4A]" data-testid="transaction-payment-select">
                      <SelectValue placeholder="Seleccionar método" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#FEE2E2] border-[#FECDD3]">
                      {paymentMethods.map((pm) => (
                        <SelectItem key={pm.id} value={pm.id} className="text-[#4A4A4A] hover:bg-[#FEE2E2]">
                          {pm.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Branch (Sucursal) */}
                <div className="space-y-2">
                  <Label className="text-[#6B7280]">Sucursal *</Label>
                  <Select value={formData.branch_id} onValueChange={(v) => setFormData({...formData, branch_id: v})}>
                    <SelectTrigger className="bg-[#FEE2E2] border-[#FECDD3] text-[#4A4A4A]" data-testid="transaction-branch-select">
                      <SelectValue placeholder="Seleccionar sucursal" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#FEE2E2] border-[#FECDD3]">
                      {branches.map((b) => (
                        <SelectItem key={b.id} value={b.id} className="text-[#4A4A4A] hover:bg-[#FEE2E2]">
                          <span className="inline-flex items-center gap-2">
                            <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: b.color }} />
                            {b.name}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Asset */}
                <div className="space-y-2">
                  <Label className="text-[#6B7280]">Activo (opcional)</Label>
                  <Input
                    value={formData.asset}
                    onChange={(e) => setFormData({...formData, asset: e.target.value})}
                    placeholder="Ej: Local, Vehículo"
                    data-testid="transaction-asset-input"
                    className="bg-[#FEE2E2] border-[#FECDD3] text-[#4A4A4A] placeholder:text-[#6B7280]/50"
                  />
                </div>

                {/* Invoiced */}
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="invoiced"
                    checked={formData.invoiced}
                    onCheckedChange={(checked) => setFormData({...formData, invoiced: checked})}
                    data-testid="transaction-invoiced-checkbox"
                    className="border-[#FECDD3] data-[state=checked]:bg-[#F43F5E]"
                  />
                  <Label htmlFor="invoiced" className="text-[#6B7280] cursor-pointer">¿Facturado?</Label>
                </div>

                {/* Notes */}
                <div className="space-y-2">
                  <Label className="text-[#6B7280]">Notas (opcional)</Label>
                  <Textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                    placeholder="Notas adicionales..."
                    data-testid="transaction-notes-input"
                    className="bg-[#FEE2E2] border-[#FECDD3] text-[#4A4A4A] placeholder:text-[#6B7280]/50 min-h-[80px]"
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1 bg-[#FEE2E2] border-[#FECDD3] text-[#4A4A4A] hover:bg-[#FEE2E2]">
                    Cancelar
                  </Button>
                  <Button type="submit" className="flex-1 bg-[#F43F5E] hover:bg-[#F43F5E]/80 text-white" data-testid="save-transaction-button">
                    {editingTransaction ? "Actualizar" : "Guardar"}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {/* Quick Entry Form - Para desactivar, comenta este bloque */}
        <QuickEntryForm
          categories={categories}
          paymentMethods={paymentMethods}
          branches={branches}
          onSave={handleQuickSave}
        />

        {/* Filters */}
        <Card className="bg-white border-[#FECDD3]">
          <CardContent className="p-4">
            {/* Mobile Filters */}
            <div className="lg:hidden space-y-3">
              <div className="flex items-center gap-2 mb-2">
                <Filter className="w-4 h-4 text-[#6B7280]" />
                <span className="text-[#6B7280] text-sm font-medium">Filtros</span>
              </div>
              
              {/* Search - Full Width on Mobile */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B7280]/50" />
                <Input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Buscar transacción..."
                  className="pl-9 bg-[#FEE2E2] border-[#FECDD3] text-[#4A4A4A] placeholder:text-[#6B7280]/50"
                />
              </div>
              
              {/* Date Range Picker */}
              <DateRangePicker />
              
              {/* Category Filter */}
              <div className="w-full">
                <Select value={filterCategory} onValueChange={setFilterCategory}>
                  <SelectTrigger className="bg-[#FEE2E2] border-[#FECDD3] text-[#4A4A4A]">
                    <SelectValue placeholder="Categoría" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-[#FECDD3]">
                    <SelectItem value="all" className="text-[#4A4A4A]">Todas las categorías</SelectItem>
                    {expenseCategories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id} className="text-[#4A4A4A]">
                        {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Desktop Filters */}
            <div className="hidden lg:flex flex-wrap gap-4 items-center">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-[#6B7280]" />
                <span className="text-[#6B7280] text-sm">Filtros:</span>
              </div>
              
              <DateRangePicker />

              <Select value={filterCategory} onValueChange={setFilterCategory}>
                <SelectTrigger className="w-40 bg-[#FEE2E2] border-[#FECDD3] text-[#4A4A4A]" data-testid="filter-category">
                  <SelectValue placeholder="Categoría" />
                </SelectTrigger>
                <SelectContent className="bg-white border-[#FECDD3]">
                  <SelectItem value="all" className="text-[#4A4A4A] hover:bg-[#FEE2E2]">Todas las categorías</SelectItem>
                  {expenseCategories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id} className="text-[#4A4A4A] hover:bg-[#FEE2E2]">
                      {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={filterPaymentMethod} onValueChange={setFilterPaymentMethod}>
                <SelectTrigger className="w-44 bg-[#FEE2E2] border-[#FECDD3] text-[#4A4A4A]" data-testid="filter-payment-method">
                  <SelectValue placeholder="Método de pago" />
                </SelectTrigger>
                <SelectContent className="bg-white border-[#FECDD3]">
                  <SelectItem value="all" className="text-[#4A4A4A] hover:bg-[#FEE2E2]">Todos los métodos</SelectItem>
                  {paymentMethods.map((pm) => (
                    <SelectItem key={pm.id} value={pm.id} className="text-[#4A4A4A] hover:bg-[#FEE2E2]">
                      {pm.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={filterBranch} onValueChange={setFilterBranch}>
                <SelectTrigger className="w-48 bg-[#FEE2E2] border-[#FECDD3] text-[#4A4A4A]" data-testid="filter-branch">
                  <SelectValue placeholder="Sucursal" />
                </SelectTrigger>
                <SelectContent className="bg-white border-[#FECDD3]">
                  <SelectItem value="all" className="text-[#4A4A4A] hover:bg-[#FEE2E2]">Todas las sucursales</SelectItem>
                  {branches.map((b) => (
                    <SelectItem key={b.id} value={b.id} className="text-[#4A4A4A] hover:bg-[#FEE2E2]">
                      <span className="inline-flex items-center gap-2">
                        <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: b.color }} />
                        {b.name}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B7280]/50" />
                  <Input
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Buscar gasto..."
                    data-testid="search-transactions"
                    className="pl-9 bg-[#FEE2E2] border-[#FECDD3] text-[#4A4A4A] placeholder:text-[#6B7280]/50"
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Charts Row - Expenses Summary */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Expenses by Category */}
          <Card className="bg-white border-[#FECDD3] shadow-sm" data-testid="expenses-by-category-chart">
            <CardHeader>
              <CardTitle className="text-[#4A4A4A] font-['Manrope']">Gastos por Categoría</CardTitle>
            </CardHeader>
            <CardContent>
              {expensesByCategory.length > 0 ? (
                <div className="flex items-center gap-4">
                  <div className="h-72 w-1/2">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={expensesByCategory}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          outerRadius={90}
                          innerRadius={40}
                        >
                          {expensesByCategory.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color || CHART_COLORS[index % CHART_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value) => formatCurrency(value)} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="w-1/2 space-y-2 max-h-72 overflow-y-auto pr-2">
                    {[...expensesByCategory]
                      .sort((a, b) => b.value - a.value)
                      .map((item, index) => (
                      <div key={index} className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-3 h-3 rounded-full flex-shrink-0" 
                            style={{ backgroundColor: item.color || CHART_COLORS[index % CHART_COLORS.length] }}
                          />
                          <span className="text-[#6B7280] truncate">{item.name}</span>
                        </div>
                        <span className="text-[#4A4A4A] font-mono ml-2">{formatCurrency(item.value)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="h-72 flex items-center justify-center text-[#9CA3AF]">
                  No hay datos de gastos para este período
                </div>
              )}
            </CardContent>
          </Card>

          {/* Expenses by Payment Method */}
          <Card className="bg-white border-[#FECDD3] shadow-sm" data-testid="expenses-by-payment-chart">
            <CardHeader>
              <CardTitle className="text-[#4A4A4A] font-['Manrope']">Gastos por Método de Pago</CardTitle>
            </CardHeader>
            <CardContent>
              {expensesByPaymentMethod.length > 0 ? (
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={expensesByPaymentMethod} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#FEE2E2" />
                      <XAxis type="number" tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} stroke="#6B7280" />
                      <YAxis type="category" dataKey="name" stroke="#6B7280" width={120} />
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                      <Bar dataKey="value" fill="#F43F5E" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-72 flex items-center justify-center text-[#9CA3AF]">
                  No hay datos de gastos para este período
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Transactions - Mobile Cards / Desktop Table */}
        <Card className="bg-white border-[#FECDD3]">
          {selectedIds.length > 0 && (
            <div className="p-4 bg-[#F43F5E]/10 border-b border-[#F43F5E]/30 flex items-center justify-between flex-wrap gap-3">
              <span className="text-[#F43F5E] font-medium">
                {selectedIds.length} transaccion{selectedIds.length > 1 ? 'es' : ''} seleccionada{selectedIds.length > 1 ? 's' : ''}
              </span>
              <div className="flex items-center gap-2 flex-wrap">
                <Select value={bulkCategoryId} onValueChange={setBulkCategoryId}>
                  <SelectTrigger className="bg-white border-[#F43F5E]/40 w-[200px]" data-testid="bulk-category-select">
                    <SelectValue placeholder="Mover a categoría..." />
                  </SelectTrigger>
                  <SelectContent className="bg-white max-h-[300px]">
                    {[...categories]
                      .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'es', { sensitivity: 'base' }))
                      .map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                <Button
                  size="sm"
                  onClick={handleBulkUpdateCategory}
                  disabled={bulkUpdating || !bulkCategoryId}
                  data-testid="bulk-update-category-button"
                  className="bg-[#3B82F6] hover:bg-[#2563EB] text-white"
                >
                  {bulkUpdating ? (
                    <span className="flex items-center gap-2">
                      <div className="spinner w-4 h-4"></div>
                      Actualizando...
                    </span>
                  ) : (
                    <>
                      <Pencil className="w-4 h-4 mr-2" />
                      Mover
                    </>
                  )}
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleBulkDelete}
                  disabled={deletingBulk}
                  data-testid="bulk-delete-button"
                  className="bg-[#F43F5E] hover:bg-[#F43F5E]/80"
                >
                  {deletingBulk ? (
                    <span className="flex items-center gap-2">
                      <div className="spinner w-4 h-4"></div>
                      Eliminando...
                    </span>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4 mr-2" />
                      Eliminar
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-48">
                <div className="spinner w-8 h-8"></div>
              </div>
            ) : filteredTransactions.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-48 text-[#6B7280]">
                <p>No hay transacciones para mostrar</p>
                <p className="text-sm">Agrega una nueva transacción para comenzar</p>
              </div>
            ) : (
              <>
                {/* Mobile View - Cards */}
                <div className="lg:hidden p-4 space-y-3">
                  {/* Select All for Mobile */}
                  <div className="flex items-center justify-between pb-3 border-b border-[#FEE2E2]">
                    <label className="flex items-center gap-2 text-sm text-[#6B7280]">
                      <Checkbox
                        checked={selectedIds.length === filteredTransactions.length && filteredTransactions.length > 0}
                        onCheckedChange={handleSelectAll}
                        className="border-[#FECDD3] data-[state=checked]:bg-[#F43F5E]"
                      />
                      Seleccionar todas
                    </label>
                    <span className="text-sm text-[#6B7280]">{filteredTransactions.length} transacciones</span>
                  </div>
                  
                  {filteredTransactions.map((t) => (
                    <div 
                      key={t.id} 
                      data-testid={`transaction-card-${t.id}`}
                      className={`p-4 rounded-lg border ${selectedIds.includes(t.id) ? 'border-[#F43F5E] bg-[#F43F5E]/5' : 'border-[#FEE2E2] bg-[#FFFAFA]'}`}
                    >
                      {/* Top row: Checkbox, Date, Amount */}
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <Checkbox
                            checked={selectedIds.includes(t.id)}
                            onCheckedChange={(checked) => handleSelectOne(t.id, checked)}
                            className="border-[#FECDD3] data-[state=checked]:bg-[#F43F5E]"
                          />
                          <div>
                            <span className="text-sm text-[#6B7280] font-mono">{format(parseISO(t.date), "dd/MM/yyyy")}</span>
                          </div>
                        </div>
                        <span className={`font-mono font-bold text-lg ${t.type === 'expense' ? 'text-[#EF4444]' : 'text-[#10B981]'}`}>
                          {t.type === 'expense' ? '-' : '+'}{formatCurrency(t.amount)}
                        </span>
                      </div>
                      
                      {/* Description */}
                      <div className="mb-3">
                        <p className="text-[#4A4A4A] font-medium">{t.description}</p>
                        {t.asset && <p className="text-[#6B7280] text-sm">Activo: {t.asset}</p>}
                        {t.notes && <p className="text-[#9CA3AF] text-sm mt-1">{t.notes}</p>}
                      </div>
                      
                      {/* Category and Payment Method */}
                      <div className="flex flex-wrap gap-2 mb-3">
                        <span 
                          className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium"
                          style={{ backgroundColor: `${categories.find(c => c.id === t.category_id)?.color}20`, color: categories.find(c => c.id === t.category_id)?.color }}
                        >
                          <span 
                            className="w-2 h-2 rounded-full" 
                            style={{ backgroundColor: categories.find(c => c.id === t.category_id)?.color }}
                          ></span>
                          {t.category_name}
                        </span>
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-[#F3F4F6] text-[#6B7280]">
                          {t.payment_method_name}
                        </span>
                        {t.invoiced && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-[#D1FAE5] text-[#10B981]">
                            Facturado
                          </span>
                        )}
                      </div>
                      
                      {/* Actions and User */}
                      <div className="flex items-center justify-between pt-3 border-t border-[#FEE2E2]">
                        <span className="text-xs text-[#9CA3AF]">Por: {t.user_name}</span>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(t)}
                            className="h-8 w-8 p-0 text-[#6B7280] hover:text-[#4A4A4A] hover:bg-[#FEE2E2]"
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(t.id)}
                            className="h-8 w-8 p-0 text-[#6B7280] hover:text-[#F43F5E] hover:bg-[#F43F5E]/10"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Desktop View - Table */}
                <div className="hidden lg:block overflow-x-auto">
                  <table className="data-table" data-testid="transactions-table">
                    <thead>
                      <tr className="bg-[#FEE2E2]/50">
                        <th className="w-12">
                          <Checkbox
                            checked={selectedIds.length === filteredTransactions.length && filteredTransactions.length > 0}
                            onCheckedChange={handleSelectAll}
                            data-testid="select-all-checkbox"
                            className="border-[#FECDD3] data-[state=checked]:bg-[#F43F5E]"
                          />
                        </th>
                        <th>Fecha</th>
                        <th>Descripción</th>
                        <th>Categoría</th>
                        <th>Sucursal</th>
                        <th>Método</th>
                        <th>Monto</th>
                        <th>Facturado</th>
                        <th>Registrado por</th>
                        <th>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredTransactions.map((t) => (
                        <tr key={t.id} data-testid={`transaction-row-${t.id}`} className={selectedIds.includes(t.id) ? 'bg-[#F43F5E]/5' : ''}>
                          <td>
                            <Checkbox
                              checked={selectedIds.includes(t.id)}
                              onCheckedChange={(checked) => handleSelectOne(t.id, checked)}
                              data-testid={`select-transaction-${t.id}`}
                              className="border-[#FECDD3] data-[state=checked]:bg-[#F43F5E]"
                            />
                          </td>
                          <td className="font-mono text-sm">{format(parseISO(t.date), "dd/MM/yyyy")}</td>
                          <td>
                            <div>
                              <span className="text-[#4A4A4A]">{t.description}</span>
                              {t.asset && <span className="text-[#6B7280] text-sm ml-2">({t.asset})</span>}
                            </div>
                            {t.notes && <p className="text-[#6B7280]/70 text-xs mt-1">{t.notes}</p>}
                          </td>
                          <td>
                            <span 
                              className="inline-flex items-center gap-2 px-2 py-1 rounded text-sm"
                              style={{ backgroundColor: `${categories.find(c => c.id === t.category_id)?.color}20` }}
                            >
                              <span 
                                className="w-2 h-2 rounded-full" 
                                style={{ backgroundColor: categories.find(c => c.id === t.category_id)?.color }}
                              ></span>
                              {t.category_name}
                            </span>
                          </td>
                          <td>
                            {t.branch_name ? (
                              <span
                                className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium border"
                                style={{
                                  color: t.branch_color,
                                  borderColor: `${t.branch_color}55`,
                                  backgroundColor: `${t.branch_color}12`,
                                }}
                              >
                                <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: t.branch_color }} />
                                {t.branch_name}
                              </span>
                            ) : (
                              <span className="text-[#9CA3AF] text-xs italic">—</span>
                            )}
                          </td>
                          <td className="text-[#6B7280]">{t.payment_method_name}</td>
                          <td>
                            <span className={`font-mono font-medium ${t.type === 'expense' ? 'text-[#F43F5E]' : 'text-[#10B981]'}`}>
                              {t.type === 'expense' ? '-' : '+'}{formatCurrency(t.amount)}
                            </span>
                          </td>
                          <td>
                            <span className={`text-xs px-2 py-1 rounded ${t.invoiced ? 'bg-[#10B981]/10 text-[#10B981]' : 'bg-[#FEE2E2] text-[#6B7280]'}`}>
                              {t.invoiced ? 'Sí' : 'No'}
                            </span>
                          </td>
                          <td className="text-[#6B7280] text-sm">{t.user_name}</td>
                          <td>
                            <div className="flex items-center gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEdit(t)}
                                data-testid={`edit-transaction-${t.id}`}
                                className="text-[#6B7280] hover:text-[#4A4A4A] hover:bg-[#FEE2E2]"
                              >
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDelete(t.id)}
                                data-testid={`delete-transaction-${t.id}`}
                                className="text-[#6B7280] hover:text-[#F43F5E] hover:bg-[#F43F5E]/10"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default TransactionsPage;
